// Audio for Artemis's Transcribe endpoint (API-3691): 16-bit mono PCM WAV, ideally at 16 kHz.
// MediaRecorder's WebM isn't accepted, so the panel records raw samples and wraps them here.

export const TARGET_RATE = 16000

// Float samples at `fromRate` → at `toRate`, averaging the samples each output one covers.
export const downsample = (samples, fromRate, toRate = TARGET_RATE) => {
  if (toRate >= fromRate) return samples
  const ratio = fromRate / toRate
  const out = new Float32Array(Math.floor(samples.length / ratio))
  for (let i = 0; i < out.length; i++) {
    const start = Math.floor(i * ratio), end = Math.min(samples.length, Math.floor((i + 1) * ratio))
    let sum = 0
    for (let j = start; j < end; j++) sum += samples[j]
    out[i] = end > start ? sum / (end - start) : 0
  }
  return out
}

// Float samples (−1..1) at `rate` as a 16-bit mono PCM WAV file's bytes.
export const encodeWav = (samples, rate = TARGET_RATE) => {
  const buffer = new ArrayBuffer(44 + samples.length * 2)
  const view = new DataView(buffer)
  const text = (offset, s) => { for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i)) }
  text(0, 'RIFF')
  view.setUint32(4, 36 + samples.length * 2, true)
  text(8, 'WAVE')
  text(12, 'fmt ')
  view.setUint32(16, 16, true) // PCM chunk size
  view.setUint16(20, 1, true) // PCM
  view.setUint16(22, 1, true) // mono
  view.setUint32(24, rate, true)
  view.setUint32(28, rate * 2, true) // byte rate
  view.setUint16(32, 2, true) // block align
  view.setUint16(34, 16, true) // bits per sample
  text(36, 'data')
  view.setUint32(40, samples.length * 2, true)
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]))
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true)
  }
  return new Uint8Array(buffer)
}

// Joins recorded chunks into one Float32Array.
export const joinChunks = chunks => {
  const out = new Float32Array(chunks.reduce((n, c) => n + c.length, 0))
  let offset = 0
  chunks.forEach(c => { out.set(c, offset); offset += c.length })
  return out
}

// Root-mean-square level of a chunk, 0..1.
export const levelOf = chunk => {
  let sum = 0
  for (let i = 0; i < chunk.length; i++) sum += chunk[i] * chunk[i]
  return chunk.length ? Math.sqrt(sum / chunk.length) : 0
}

// Ends a recording on a pause after speech, as the browser recogniser did: feed it each chunk's
// level and duration; it answers true once there's been speech and then `silenceMs` of quiet.
export const pauseDetector = ({ speechLevel = 0.02, silenceMs = 1500, maxMs = 60000 } = {}) => {
  let heard = false, quietFor = 0, total = 0
  return (level, ms) => {
    total += ms
    if (total >= maxMs) return true
    if (level >= speechLevel) { heard = true; quietFor = 0; return false }
    if (heard) quietFor += ms
    return heard && quietFor >= silenceMs
  }
}
